import type { NavGroupLink } from '@/features/common/types/NavGroupLink';
import type { PublicPageLink } from '@/features/common/types/PublicPageLink';

/**
 * The administered pages the site chrome links to by name. The gov-bar's „Accesibilitate” opened
 * the presentation page, because there was no accessibility statement to open (T143).
 */
export const SITE_PAGE = {
  presentation: '/pagini/despre',
  accessibility: '/pagini/accesibilitate',
} as const;

/**
 * A PAGE the footer reads as a fragment — the EU visibility strip — rather than a page anyone
 * should navigate to.
 */
export const EU_VISIBILITY_SLUG = 'eu-visibility';

export function pagePath(slug: string): string {
  return `/pagini/${encodeURIComponent(slug)}`;
}

/**
 * Where a published content item lives on the public site, by its kind: news and articles share
 * /stiri/{slug} (the server resolves NEWS first on a collision, see publicContentApi), a page is
 * pagePath, and a policy has /politici/{slug}. Null for a kind the site has no page for, so a
 * caller offers no link rather than one that 404s.
 */
export function contentPath(kind: string, slug: string): string | null {
  const encoded = encodeURIComponent(slug);
  switch (kind.toUpperCase()) {
    case 'NEWS':
    case 'ARTICLE':
      return `/stiri/${encoded}`;
    case 'PAGE':
      return pagePath(slug);
    case 'POLICY':
      return `/politici/${encoded}`;
    default:
      return null;
  }
}

/**
 * The published pages the menu and the sitemap do not already name, as links (T143: a page
 * created in the admin appeared in neither, because both were written in code).
 *
 * `lang` marks a title served in Romanian to an English reader, so a screen reader switches
 * voice for it (WCAG 3.1.2) — the same rule ContentPage applies to the page itself.
 */
export function pageLinksBeyond(
  pages: PublicPageLink[],
  alreadyLinked: readonly string[],
  language: string,
): NavGroupLink[] {
  return pages
    .filter((page) => page.slug !== EU_VISIBILITY_SLUG)
    .map((page) => {
      const contentLanguage = page.contentLanguage.toLowerCase();
      return {
        to: pagePath(page.slug),
        label: page.title,
        lang: contentLanguage !== language ? contentLanguage : undefined,
      };
    })
    .filter((link) => !alreadyLinked.includes(link.to));
}
